import React, { useState, useRef, FC } from "react";
import { FormattedMessage } from "react-intl";
import Draggable from "react-draggable";
import { CloseOutlined, PrinterOutlined } from "@ant-design/icons";
import { Modal, Button, Typography, Table, Tag } from "antd";
import moment from "moment";
import { useQuery } from "react-query";
import { getEmployeeEffectivePlan } from "../../../api/server";
import { getFullName } from "../../../components/util/helpers";
import { useIntl } from "react-intl";
import { useReactToPrint } from "react-to-print";
import {
  OrderPlanModalProps,
  TableDataRow,
  PlanItem,
  TransformedPlanItem,
  TimeRange,
} from "../types";

const { Text, Title } = Typography;

/**
 * OrderPlanModal Component
 *
 * A draggable modal component that displays an employee's work schedule for a specific month.
 * The schedule is presented as a weekly calendar view with color-coded client assignments.
 *
 * Features:
 * - Displays employee's work schedule in a weekly calendar format
 * - Shows patient names and color-coded client information
 * - Provides time ranges for each appointment
 * - Calculates duration totals per day and week
 * - Supports printing of the schedule
 * - Draggable modal interface for flexible positioning
 *
 * The component fetches data using React Query and transforms it into a table-friendly format,
 * with weekdays as columns and time slots as rows.
 *
 * @example
 * ```tsx
 * <OrderPlanModal
 *   employee={employeeData}
 *   onHide={() => setModalVisible(false)}
 *   visible={modalVisible}
 *   activeYear={2023}
 *   activeMonth={5}
 * />
 * ```
 */
const OrderPlanModal: FC<OrderPlanModalProps> = ({
  employee,
  onHide,
  visible,
  activeYear,
  activeMonth,
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const [isDraggableDisabled, setIsDraggableDisabled] = useState<boolean>(true);
  const [totalForWeek, setTotalForWeek] = useState<string | null>(null);
  const intl = useIntl();
  const employeeName = employee
    ? getFullName(employee.surename, employee.first_name)
    : "";
  const clientColors = useRef<Record<string, string>>({}).current;
  const colors: string[] = ["cyan", "blue", "geekblue", "purple"];

  // API query configuration
  const { isLoading, data } = useQuery({
    queryKey: ["employeePlan", employee?.id, activeYear, activeMonth],
    queryFn: async () => {
      if (!employee) throw new Error("Employee is required");

      const params = {
        employeeId: employee.id,
        query: { year: +activeYear, month: +activeMonth },
      };
      const result = await getEmployeeEffectivePlan(params);
      const transformed = transformData(result.data.items);
      const totalHours = formatDuration(
        result.data.items.reduce(
          (sum: number, item: PlanItem) => sum + item.duration_minutes,
          0
        )
      );
      setTotalForWeek(totalHours);
      return transformed;
    },
    enabled: !!employee,
    staleTime: 0,
  });

  /**
   * Transforms raw plan items into a structured format with client and patient information.
   *
   * @param {PlanItem[]} items - Raw plan items from the API
   * @returns {TableDataRow[]} Transformed data ready for table display
   */
  const transformData = (items: PlanItem[]): TableDataRow[] => {
    const uniqueEvents: TransformedPlanItem[] = [
      ...new Map(items.map((item) => [item.id, item])).values(),
    ]
      .filter((item) => item.order)
      .map((item) => ({
        ...item,
        client_name: item.order?.client?.name || null,
        patient_surename: item.order?.patient?.surename || null,
        patient_first_name: item.order?.patient?.first_name || null,
      }));
    return transformToTableData(uniqueEvents);
  };

  /**
   * Transforms event data into a table-friendly format with weekdays as columns.
   *
   * @param {TransformedPlanItem[]} events - Transformed event items
   * @returns {TableDataRow[]} Data structured for table rows
   */
  const transformToTableData = (
    events: TransformedPlanItem[]
  ): TableDataRow[] => {
    const weekdayEvents: Array<TransformedPlanItem[]> = Array(7)
      .fill(null)
      .map(() => []);

    events
      .sort((a, b) => a.weekday - b.weekday)
      .forEach((event) => weekdayEvents[event.weekday].push(event));

    weekdayEvents.forEach((day) =>
      day.sort((a, b) => a.start_time_minutes - b.start_time_minutes)
    );

    return Array.from(
      { length: Math.max(...weekdayEvents.map((day) => day.length), 0) },
      (_, i) =>
        Object.fromEntries(
          weekdayEvents.map((day, j) => [j, day[i] || null])
        ) as TableDataRow
    );
  };

  /**
   * Formats duration minutes as hours with decimal precision.
   *
   * @param {number} minutes - Duration in minutes
   * @returns {string} Formatted duration in hours
   */
  const formatDuration = (minutes: number): string => (minutes / 60).toFixed(2);

  /**
   * Calculates the start and end time for a schedule record.
   *
   * @param {PlanItem} record - Schedule record containing time information
   * @returns {TimeRange} Object with formatted start and end times
   */
  const getTimeRange = (record: PlanItem): TimeRange => ({
    start: moment()
      .startOf("day")
      .minutes(record.start_time_minutes)
      .format("HH:mm"),
    end: moment()
      .startOf("day")
      .minutes(record.start_time_minutes + record.duration_minutes)
      .format("HH:mm"),
  });

  /**
   * Renders the content for each table cell, including time range, patient name, and client tag.
   *
   * @param {PlanItem | null} record - Schedule record to render, or null for empty cells
   * @returns {React.ReactNode} The rendered cell content
   */
  const renderCellContent = (record: PlanItem | null) => {
    if (!record) return null;
    const { start, end } = getTimeRange(record);
    const fullName = getFullName(
      record.patient_surename || "",
      record.patient_first_name || ""
    );

    if (record.client_name && !clientColors[record.client_name]) {
      clientColors[record.client_name] =
        colors[Object.keys(clientColors).length % colors.length];
    }

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Text strong>{`${end}-${start}`}</Text>
        <Text>{fullName}</Text>
        {record.client_name && (
          <Tag
            color={clientColors[record.client_name]}
            style={{
              width: "auto",
              maxWidth: "100%",
              marginTop: "10px",
              whiteSpace: "normal",
              height: "auto",
              lineHeight: "1.2em",
              padding: "4px 8px",
              wordBreak: "break-word",
            }}
          >
            {record.client_name}
          </Tag>
        )}
      </div>
    );
  };

  // Define table columns based on weekdays
  const columns = moment.weekdays().map((day: string, index: number) => ({
    title: () => <Text strong>{day}</Text>,
    dataIndex: String(index),
    key: String(index),
    render: renderCellContent,
  }));

  /**
   * Renders the summary row with total hours per day.
   *
   * @param {readonly TableDataRow[]} pageData - The current page data
   * @returns {React.ReactNode} The summary row component
   */
  const renderSummary = (pageData: readonly TableDataRow[]) => (
    <Table.Summary.Row>
      {columns.map((column, index) => (
        <Table.Summary.Cell key={column.key} index={index}>
          <Title level={5}>
            {formatDuration(
              pageData.reduce(
                (sum, record) =>
                  sum + (record[column.key]?.duration_minutes || 0),
                0
              )
            )}
          </Title>
        </Table.Summary.Cell>
      ))}
    </Table.Summary.Row>
  );

  // Configure print handler with custom page settings
  const handlePrint = useReactToPrint({
    pageStyle: `
      @page { size: landscape; margin: 0mm; }
      @media print { 
        body { 
          -webkit-print-color-adjust: exact; 
          padding: 40px !important; 
          direction: rtl; 
        } 
      }
    `,
    content: () => componentRef.current,
  });

  /**
   * Handles toggling of draggable functionality for the modal.
   *
   * @param {boolean} enabled - Whether dragging should be enabled
   * @returns {() => void} Event handler function
   */
  const handleDragToggle = (enabled: boolean) => () =>
    setIsDraggableDisabled(!enabled);

  return (
    <Modal
      open={visible}
      closable={false}
      footer={null}
      maskClosable={false}
      width="fit-content"
      style={{ top: 20 }}
      styles={{ body: { padding: 0 } }}
      destroyOnClose
      modalRender={(modal) => (
        <Draggable disabled={isDraggableDisabled}>{modal}</Draggable>
      )}
    >
      <div>
        {/* Header for dragging */}
        <div
          style={{
            width: "100%",
            height: "50px",
            position: "absolute",
            cursor: "move",
            left: "0",
            top: "0",
          }}
          onMouseOver={handleDragToggle(true)}
          onMouseOut={handleDragToggle(false)}
        >
          <Button
            style={{ position: "absolute", left: 10, top: 10 }}
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onHide}
          />
        </div>

        {/* Print button */}
        <div style={{ position: "absolute", left: "80px", top: "80px" }}>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            <FormattedMessage id="app.actions.print" />
          </Button>
        </div>

        {/* Main content */}
        <div style={{ padding: "40px 80px" }}>
          <div ref={componentRef}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div>
                <Title
                  style={{
                    fontSize: "30px",
                    fontWeight: 500,
                    lineHeight: 1.27,
                    margin: 0,
                  }}
                >
                  {`${intl.formatMessage({
                    id: "app.salary.order_plan_title",
                  })} ${activeMonth}/${activeYear}`}
                </Title>
                <Title level={3} style={{ margin: "0 0 10px 0" }}>
                  {employeeName}
                </Title>
              </div>
            </div>

            <Table
              loading={isLoading}
              bordered
              dataSource={data}
              columns={columns}
              pagination={false}
              scroll={{ y: window.innerHeight - 200 }}
              style={{ width: "100%", padding: "20px 0" }}
              summary={renderSummary}
            />

            <Title level={3} style={{ margin: "10px 0" }}>
              {`${intl.formatMessage({
                id: "app.salary.totalForWeek",
              })}: ${totalForWeek}`}
            </Title>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OrderPlanModal;
